// Botão "Login" do card Controle de Usuário (tela Usuários) — pedido
// 2026-09-23: "enviar login para o cliente, usa o número conectado da
// empresa". Envia pelo WhatsApp conectado em Administração → Integrações.
// Backend: POST /api/v1/users/:id/send-login (client-contracts/send-login.ts).
//
// Simplificado em 2026-09-25 (pedido do usuário): virou só uma confirmação.
// Texto = Mensagem de Boas-Vindas (Administração → Comunicação), celular =
// o do cadastro (não editável aqui — muda editando o cadastro), senha
// sempre redefinida para 123456 (o cliente troca depois se quiser). O
// dryRun ao abrir valida tudo isso e devolve o número mostrado.
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Loader2, MessageCircle, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { gpsDrivePost, GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { useWhatsappIntegration } from "@/hooks/queries";

interface SendLoginInput {
  loginUrl?: string;
  dryRun?: boolean;
}

interface SendLoginResult {
  sent: boolean;
  phone: string;
  message: string;
  passwordChanged: boolean;
}

const sendLogin = (userId: string, input: SendLoginInput) =>
  gpsDrivePost<SendLoginResult>(`/api/v1/users/${encodeURIComponent(userId)}/send-login`, input);

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

export function SendLoginDialog({
  open,
  onOpenChange,
  userId,
  clientName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  clientName: string;
}) {
  const { data: whatsapp, isError: whatsappUnknown } = useWhatsappIntegration();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const loginUrl = typeof window !== "undefined" && /^https?:/.test(window.location.origin) ? window.location.origin : undefined;

  // Sempre fresco ao abrir — o celular/Boas-Vindas podem ter acabado de
  // ser editados.
  const check = useQuery({
    queryKey: ["send-login-check", userId],
    queryFn: () => sendLogin(userId, { loginUrl, dryRun: true }),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const send = useMutation({
    mutationFn: () => sendLogin(userId, { loginUrl }),
    onSuccess: (res) => {
      onOpenChange(false);
      toast.success(`Login enviado para ${formatPhone(res.phone)}`);
    },
    onError: (err) => setError(err instanceof GpsDriveApiError || err instanceof Error ? err.message : "Falha ao enviar"),
  });

  const notConnected = !whatsappUnknown && whatsapp !== undefined && !whatsapp.connected;
  const checkError = check.error instanceof Error ? check.error.message : check.error ? "Não foi possível preparar o envio" : null;
  const missingWelcome = checkError?.includes("Boas-Vindas");
  const canSend = Boolean(check.data) && !notConnected && !send.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-600" /> Enviar login
          </DialogTitle>
          <DialogDescription>
            Tem certeza que deseja enviar os dados de acesso de <b>{clientName}</b> pelo WhatsApp?
          </DialogDescription>
        </DialogHeader>

        {notConnected && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              O WhatsApp da empresa não está conectado.{" "}
              <Link to="/admin/integrations" className="font-semibold underline">
                Conectar em Integrações
              </Link>
            </span>
          </div>
        )}

        {check.isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : checkError ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {checkError}
              {missingWelcome && (
                <>
                  {" "}
                  <Link to="/admin/communication" className="font-semibold underline">
                    Configurar agora
                  </Link>
                </>
              )}
            </span>
          </div>
        ) : check.data ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3 rounded-md border bg-muted/40 p-3">
              <Phone className="h-4 w-4 shrink-0 text-emerald-600" />
              <div>
                <p className="text-xs text-muted-foreground">Será enviado para</p>
                <p className="text-base font-semibold">{formatPhone(check.data.phone)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              A senha do cliente será redefinida para <b>123456</b> — ele pode trocar depois. Para mudar o
              número, edite o cadastro do cliente.
            </p>
          </div>
        ) : null}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => send.mutate()} disabled={!canSend} className="bg-emerald-600 hover:bg-emerald-700">
            {send.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
